import React, { useMemo } from "react";
import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import DurationInput, { formatDuration } from "../../../components/common/DurationInput";

const ORDINARY_PAUSE_TYPES = new Set([
  "choice",
  "button_choice",
  "input_capture",
  "browse_catalogue",
  "present_catalogue_results",
  "review_and_submit",
]);

const DURABLE_AWAITING_RESPONSE_TYPES = new Set([
  "choice",
  "button_choice",
  "template",
  "input_capture",
]);

export const isDurableAwaitingResponseNode = nodeType =>
  DURABLE_AWAITING_RESPONSE_TYPES.has(String(nodeType || "").trim().toLowerCase());

export const selectAutoReplyFollowUpMaxHorizonSeconds = (nodeType, capabilities = {}) => {
  const genericMaximum = Number(capabilities?.maxHorizonSeconds) || 0;
  const pauseMaximum = Number(capabilities?.autoReplyPauseMaxHorizonSeconds) || 0;
  return isDurableAwaitingResponseNode(nodeType) ? genericMaximum : pauseMaximum || genericMaximum;
};

export const emptyFollowUp = () => ({ enabled: false, steps: [] });
export const newFollowUpStep = () => ({
  delaySeconds: 60,
  deliveryMode: "FreeFormOnly",
  freeFormContent: "",
  templateId: null,
  templateName: "",
  templateLanguage: "",
  templateBindings: {},
});

export const isFollowUpEligibleNode = (node, edges = [], nodes = []) => {
  const type = String(node?.type || "").trim().toLowerCase();
  if (type === "schedule_booking" || type === "whatsapp_flow") return true;
  if (type === "template") {
    if (!node?.id) return false;
    const knownIds = new Set((Array.isArray(nodes) ? nodes : []).map(item => item?.id).filter(Boolean));
    knownIds.add(node.id);
    return (Array.isArray(edges) ? edges : []).some(edge =>
      edge?.source === node.id &&
      knownIds.has(edge?.target) &&
      /^button-\d+$/i.test(String(edge?.sourceHandle || "").trim())
    );
  }
  if (node?.data?.followUpEligible === true) return true;
  return ORDINARY_PAUSE_TYPES.has(type);
};

export const normalizeFollowUpForSave = (node, edges = [], nodes = []) => {
  const configured = node?.data?.followUp || null;
  if (!configured || isFollowUpEligibleNode(node, edges, nodes)) return configured;
  return { ...configured, enabled: false };
};

export const validateFollowUp = (value, maxHorizonSeconds) => {
  if (!value?.enabled) return "";
  const steps = Array.isArray(value.steps) ? value.steps : [];
  if (!steps.length) return "Add at least one follow-up step.";
  let horizon = 0;
  for (const step of steps) {
    const delay = Number(step?.delaySeconds);
    if (!Number.isInteger(delay) || delay < 1) return "Each follow-up delay must be greater than zero.";
    horizon += delay;
  }
  if (Number(maxHorizonSeconds) > 0 && horizon > Number(maxHorizonSeconds))
    return `Follow-up window exceeds the ${formatDuration(maxHorizonSeconds)} maximum.`;
  for (const step of steps) {
    const mode = String(step?.deliveryMode || "");
    const hasText = Boolean(String(step?.freeFormContent || "").trim());
    const hasTemplate = Boolean(String(step?.templateName || "").trim());
    if (mode === "Smart" && (!hasText || !hasTemplate))
      return "Smart delivery requires both a message and an approved template.";
    if (mode === "TemplateOnly" && !hasTemplate)
      return "Template only delivery requires an approved template.";
    if (mode === "FreeFormOnly" && !hasText)
      return "Free-form only delivery requires a message.";
    if (!['Smart', 'TemplateOnly', 'FreeFormOnly'].includes(mode))
      return "Choose a valid delivery mode.";
    if (String(step?.freeFormContent || "").length > 4096)
      return "A follow-up message may contain at most 4096 characters.";
  }
  return "";
};

export default function AutoReplyFollowUpEditor({ value, onChange, templates = [], maxHorizonSeconds = 0,
  description = "Send only while this customer-response pause remains active." }) {
  const config = value || emptyFollowUp();
  const steps = Array.isArray(config.steps) ? config.steps : [];
  const error = useMemo(
    () => validateFollowUp(config, maxHorizonSeconds),
    [config, maxHorizonSeconds]
  );
  const updateStep = (index, patch) => onChange({
    ...config,
    steps: steps.map((step, stepIndex) => stepIndex === index ? { ...step, ...patch } : step),
  });
  const moveStep = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= steps.length) return;
    const reordered = [...steps];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
    onChange({ ...config, steps: reordered });
  };

  return (
    <section className="space-y-3 rounded-lg border border-emerald-100 bg-emerald-50/40 p-4" aria-label="Automated follow-ups">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-800">Automated follow-ups</h3>
          <p className="text-[11px] text-slate-500">{description}</p>
        </div>
        <label className="flex items-center gap-2 text-sm font-medium text-slate-700">
          <input
            aria-label="Automated follow-ups"
            type="checkbox"
            checked={config.enabled === true}
            onChange={event => onChange({
              ...config,
              enabled: event.target.checked,
              steps: event.target.checked && !steps.length ? [newFollowUpStep()] : steps,
            })}
          />
          {config.enabled ? "On" : "Off"}
        </label>
      </div>

      {config.enabled && steps.map((step, index) => {
        const mode = String(step.deliveryMode || "FreeFormOnly");
        const templateValue = `${step.templateName || ""}|||${step.templateLanguage || ""}`;
        return (
          <div key={index} className="space-y-3 rounded-md border border-slate-200 bg-white p-3">
            <div className="flex items-center justify-between">
              <strong className="text-xs text-slate-700">Step {index + 1}</strong>
              <div className="flex gap-1">
                <button type="button" aria-label={`Move step ${index + 1} up`} disabled={index === 0} onClick={() => moveStep(index, -1)}><ArrowUp size={14} /></button>
                <button type="button" aria-label={`Move step ${index + 1} down`} disabled={index === steps.length - 1} onClick={() => moveStep(index, 1)}><ArrowDown size={14} /></button>
                <button type="button" aria-label={`Remove step ${index + 1}`} onClick={() => onChange({ ...config, steps: steps.filter((_, i) => i !== index) })}><Trash2 size={14} /></button>
              </div>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="text-xs text-slate-600">Send after
                <DurationInput seconds={step.delaySeconds ?? 60} ariaLabel={`Step ${index + 1} delay`}
                  onChange={delaySeconds => updateStep(index, { delaySeconds })} />
              </div>
              <label className="text-xs text-slate-600">Delivery
                <select aria-label={`Step ${index + 1} delivery`} value={mode}
                  onChange={event => updateStep(index, { deliveryMode: event.target.value })}
                  className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm">
                  <option value="Smart">Smart</option>
                  <option value="TemplateOnly">Template only</option>
                  <option value="FreeFormOnly">Free-form only</option>
                </select>
              </label>
            </div>
            {mode !== "FreeFormOnly" && (
              <label className="block text-xs text-slate-600">Approved template
                <select aria-label={`Step ${index + 1} template`} value={templateValue}
                  onChange={event => {
                    const [name, language] = event.target.value.split("|||");
                    const selected = templates.find(item => item.name === name && item.languageCode === language);
                    updateStep(index, {
                      templateId: selected?.id || null,
                      templateName: name || "",
                      templateLanguage: language || "",
                    });
                  }} className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm">
                  <option value="|||">Select approved template</option>
                  {templates.map(template => (
                    <option key={`${template.id || template.name}-${template.languageCode}`} value={`${template.name}|||${template.languageCode}`}>
                      {template.name} · {template.languageCode}
                    </option>
                  ))}
                </select>
              </label>
            )}
            {mode !== "TemplateOnly" && (
              <label className="block text-xs text-slate-600">Message
                <textarea aria-label={`Step ${index + 1} message`} rows="3" maxLength="4096"
                  value={step.freeFormContent || ""}
                  onChange={event => updateStep(index, { freeFormContent: event.target.value })}
                  className="mt-1 w-full rounded border border-slate-200 px-2 py-1.5 text-sm" />
              </label>
            )}
          </div>
        );
      })}

      {config.enabled && (
        <button type="button" onClick={() => onChange({ ...config, steps: [...steps, newFollowUpStep()] })}
          className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
          <Plus size={14} /> Add follow-up
        </button>
      )}
      {config.enabled && Number(maxHorizonSeconds) > 0 && (
        <div className="space-y-1 text-[11px] text-slate-500">
          <p>Follow-up window: {formatDuration(steps.reduce((sum, step) => sum + (Number(step.delaySeconds) || 0), 0))}</p>
          <p>Maximum allowed: {formatDuration(maxHorizonSeconds)}</p>
        </div>
      )}
      {error && <p role="alert" className="text-xs text-rose-600">{error}</p>}
    </section>
  );
}
